import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { useAuth } from '../auth/AuthContext';
import { apiGet, apiPost, apiPut } from '../api/client';
import cantalLogo from '../assets/cantal.png';

interface InternationalCapDto {
  team: string;
  caps: number;
  points: number;
}

interface PlayerSeasonStatsDto {
  tries: number | null;
  minutes: number | null;
  starts: number | null;
}

interface PlayerSummaryDto {
  id: string;
  externalId: string;
  firstName: string;
  lastName: string;
  position: string | null;
  heightCm: number | null;
  weightKg: number | null;
  isJiff: boolean;
  nationality: string | null;
  photoMediaId: string | null;
  birthDate: string | null;
  internationalCaps: InternationalCapDto[];
  seasonStats: PlayerSeasonStatsDto | null;
  previousClubName: string | null;
  joinedYear: string | null;
}

function calcAge(birthDate: string | null): number | null {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

interface ScrapeJobDto {
  id: string;
  status: string;
  progressDone: number;
  progressTotal: number | null;
  errors: string | null;
  startedAt: string | null;
  finishedAt: string | null;
}

interface MatchDetailDto {
  id: string;
  externalId: string;
  round: string | null;
  homeClubName: string;
  awayClubName: string;
  kickoffUtc: string | null;
  homeScore: number | null;
  awayScore: number | null;
  status: string;
  competitionId: string;
  homePlayers: PlayerSummaryDto[];
  awayPlayers: PlayerSummaryDto[];
  lastScrapeJob: ScrapeJobDto | null;
}

interface LineupEntryDto {
  number: number;
  playerId: string;
  firstName: string;
  lastName: string;
  position: string | null;
  isCaptain: boolean;
  photoMediaId: string | null;
}

interface LineupDto {
  id: string;
  side: string;
  entries: LineupEntryDto[];
}

interface LineupEntryInput {
  number: number;
  playerId: string;
  isCaptain: boolean;
}

interface PlayerNoteDto {
  playerId: string;
  content: string;
}

// ─── Pitch layout ────────────────────────────────────────────────────────────

const FORMATION_ROWS: number[][] = [
  [1, 2, 3],
  [4, 5],
  [6, 8, 7],
  [9, 10],
  [11, 12, 13, 14],
  [15],
];

const SUBS = [16, 17, 18, 19, 20, 21, 22, 23];

function formatKickoff(kickoffUtc: string | null): string {
  if (!kickoffUtc) return 'Date inconnue';
  const d = new Date(kickoffUtc);
  return d.toLocaleString('fr-FR', {
    weekday: 'short', day: 'numeric', month: 'long',
    year: 'numeric', hour: '2-digit', minute: '2-digit',
    timeZone: 'Europe/Paris',
  });
}

// ─── Player avatar ────────────────────────────────────────────────────────────

function PlayerAvatar({ photoMediaId, size = 32 }: { photoMediaId: string | null; size?: number }): JSX.Element {
  if (!photoMediaId) {
    return (
      <div style={{
        width: size, height: size, borderRadius: '50%',
        background: '#e5e7eb', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: size * 0.4, color: '#aaa',
      }}>?</div>
    );
  }
  return (
    <img
      src={`/api/media/${photoMediaId}`}
      alt=""
      style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
    />
  );
}

// ─── Slot → position keywords mapping ────────────────────────────────────────

const SLOT_POSITION_KEYWORDS: Record<number, string[]> = {
  1:  ['pilier'],
  2:  ['talonneur'],
  3:  ['pilier'],
  4:  ['deuxi', '2ème', '2e ligne', '2l'],
  5:  ['deuxi', '2ème', '2e ligne', '2l'],
  6:  ['flanker', 'troisième ligne aile', '3la', '3e ligne aile'],
  7:  ['flanker', 'troisième ligne aile', '3la', '3e ligne aile'],
  8:  ['numéro 8', 'numero 8', 'n°8', 'troisième ligne centre', '3lc', '3e ligne centre'],
  9:  ['demi de m', 'demi-m', 'mêlée', 'melee'],
  10: ["demi d'ouverture", 'demi-ouverture', 'ouverture'],
  11: ['ailier'],
  12: ['centre'],
  13: ['centre'],
  14: ['ailier'],
  15: ['arrière', 'arriere'],
};

function matchesSlotPosition(position: string | null, slotNumber: number): boolean {
  if (!position) return false;
  const pos = position.toLowerCase();
  const keywords = SLOT_POSITION_KEYWORDS[slotNumber] ?? [];
  return keywords.some(k => pos.includes(k));
}

// ─── Player picker modal ──────────────────────────────────────────────────────

interface PlayerPickerProps {
  players: PlayerSummaryDto[];
  usedPlayerIds: Set<string>;
  currentPlayerId: string | null;
  onSelect: (player: PlayerSummaryDto) => void;
  onClose: () => void;
  slotNumber: number;
}

function PlayerPicker({ players, usedPlayerIds, currentPlayerId, onSelect, onClose, slotNumber }: PlayerPickerProps): JSX.Element {
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const filtered = players
    .filter(p => {
      if (usedPlayerIds.has(p.id) && p.id !== currentPlayerId) return false;
      const q = search.toLowerCase();
      return (
        p.firstName.toLowerCase().includes(q) ||
        p.lastName.toLowerCase().includes(q) ||
        (p.position ?? '').toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      const aMatch = matchesSlotPosition(a.position, slotNumber);
      const bMatch = matchesSlotPosition(b.position, slotNumber);
      if (aMatch !== bMatch) return aMatch ? -1 : 1;
      return a.lastName.localeCompare(b.lastName, 'fr');
    });

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: '8px',
          width: '340px', maxHeight: '480px',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#00174d' }}>
            Poste #{slotNumber}
          </span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: '1.2rem', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: '0.5rem' }}>
          <input
            ref={inputRef}
            type="text"
            placeholder="Rechercher..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', boxSizing: 'border-box',
              border: '1px solid #e5e7eb', borderRadius: '4px',
              padding: '0.4rem 0.6rem', fontSize: '0.85rem',
            }}
          />
        </div>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '1rem', textAlign: 'center', color: '#aaa', fontSize: '0.85rem' }}>Aucun joueur</div>
          ) : filtered.map(p => (
            <div
              key={p.id}
              onClick={() => onSelect(p)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.6rem',
                padding: '0.5rem 1rem', cursor: 'pointer',
                background: p.id === currentPlayerId ? '#f0f4ff' : undefined,
                borderBottom: '1px solid #f5f5f5',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = '#f9fafb'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = p.id === currentPlayerId ? '#f0f4ff' : ''; }}
            >
              <PlayerAvatar photoMediaId={p.photoMediaId} size={36} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{p.firstName} {p.lastName}</div>
                <div style={{ fontSize: '0.75rem', color: '#888' }}>{p.position ?? '—'}</div>
              </div>
              {p.isJiff && (
                <span style={{ background: '#e0f2fe', color: '#0369a1', borderRadius: '3px', padding: '0 0.3rem', fontSize: '0.7rem', fontWeight: 700 }}>JIFF</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Pitch lineup editor ──────────────────────────────────────────────────────

interface PitchLineupEditorProps {
  matchId: string;
  side: 'Home' | 'Away';
  clubName: string;
  players: PlayerSummaryDto[];
  printRef?: React.Ref<HTMLDivElement>;
}

function PitchLineupEditor({ matchId, side, clubName, players, printRef }: PitchLineupEditorProps): JSX.Element {
  const [entries, setEntries] = useState<LineupEntryInput[]>([]);
  const [pickerSlot, setPickerSlot] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hoveredInfo, setHoveredInfo] = useState<{
    player: PlayerSummaryDto;
    entry: LineupEntryInput | null;
    num: number;
    rect: DOMRect;
  } | null>(null);
  const [playerNotes, setPlayerNotes] = useState<Record<string, string>>({});

  useEffect(() => {
    void (async () => {
      try {
        const data = await apiGet<{ playerNotes: PlayerNoteDto[] }>(`/matches/${matchId}/notes`);
        const map: Record<string, string> = {};
        for (const n of data.playerNotes) map[n.playerId] = n.content;
        setPlayerNotes(map);
      } catch { /* ignore */ }
    })();
  }, [matchId]);

  const saveNote = async (playerId: string, content: string) => {
    try {
      await apiPut(`/matches/${matchId}/notes/player/${playerId}`, { content });
    } catch { /* ignore */ }
  };

  useEffect(() => {
    void (async () => {
      try {
        const lineup = await apiGet<LineupDto>(`/matches/${matchId}/lineups/${side}`);
        if (lineup.entries.length > 0) {
          setEntries(lineup.entries.map(e => ({ number: e.number, playerId: e.playerId, isCaptain: e.isCaptain })));
        }
      } catch {
        setLoadError('Impossible de charger la composition');
      }
    })();
  }, [matchId, side]);

  const usedPlayerIds = new Set(entries.map(e => e.playerId));

  const getEntry = (num: number) => entries.find(e => e.number === num) ?? null;
  const getPlayer = (num: number) => {
    const e = getEntry(num);
    return e ? players.find(p => p.id === e.playerId) ?? null : null;
  };

  const assign = (num: number, player: PlayerSummaryDto) => {
    setEntries(prev => [...prev.filter(e => e.number !== num), { number: num, playerId: player.id, isCaptain: false }]);
    setSaved(false);
    setPickerSlot(null);
  };

  const toggleCaptain = (num: number) => {
    setEntries(prev => prev.map(e => e.number === num ? { ...e, isCaptain: !e.isCaptain } : { ...e, isCaptain: false }));
    setSaved(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiPut(`/matches/${matchId}/lineups/${side}`, { entries });
      setSaved(true);
    } catch {
      setLoadError('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const starters = entries.filter(e => e.number <= 15).length;

  return (
    <div style={{ flex: 1, minWidth: '280px' }}>
      <div ref={printRef}>
      {/* Header */}
      <div style={{
        background: '#00174d', color: '#fff',
        padding: '0.5rem 1rem', borderRadius: '4px 4px 0 0',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>{clubName}</span>
        <span style={{ fontSize: '0.75rem', color: '#a8c4e0' }}>{starters}/15 titulaires</span>
      </div>

      {loadError && (
        <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '0.4rem 1rem', fontSize: '0.82rem' }}>{loadError}</div>
      )}

      {/* Composition sheet */}
      <div style={{
        background: '#f9fafb',
        border: '1px solid #d1d5db',
        padding: '0.6rem 0.5rem',
      }}>
        {FORMATION_ROWS.map((row, rowIdx) => (
          <div key={rowIdx} style={{
            display: 'flex',
            gap: '0.4rem',
            justifyContent: 'center',
            marginBottom: rowIdx < FORMATION_ROWS.length - 1 ? '0.4rem' : 0,
          }}>
            {row.map(num => {
              const player = getPlayer(num);
              const entry = getEntry(num);
              const hasPlayer = player !== null;

              return (
                <div
                  key={num}
                  onClick={() => setPickerSlot(num)}
                  onMouseEnter={e => {
                    if (hasPlayer) {
                      setHoveredInfo({ player: player!, entry: getEntry(num), num, rect: (e.currentTarget as HTMLDivElement).getBoundingClientRect() });
                    }
                  }}
                  onMouseLeave={() => setHoveredInfo(null)}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    background: hasPlayer ? '#fff' : '#f3f4f6',
                    border: `1.5px solid ${entry?.isCaptain ? '#FFD700' : hasPlayer ? '#c7d2fe' : '#e5e7eb'}`,
                    borderRadius: '6px',
                    padding: '0.35rem 0.4rem',
                    cursor: 'pointer',
                    position: 'relative',
                    boxShadow: hasPlayer ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                    transition: 'border-color 0.15s',
                  }}
                >
                  {/* Captain badge */}
                  {entry?.isCaptain && (
                    <div style={{
                      position: 'absolute', top: 3, right: 3,
                      background: '#FFD700', color: '#000',
                      borderRadius: '3px', padding: '0 3px',
                      fontSize: '0.55rem', fontWeight: 900, lineHeight: '15px',
                      zIndex: 1,
                    }}>C</div>
                  )}

                  {/* Photo + info row */}
                  <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'flex-start' }}>
                    {/* Photo + number badge */}
                    <div style={{ position: 'relative', width: 38, height: 38, flexShrink: 0 }}>
                      <div style={{
                        width: 38, height: 38, borderRadius: '50%', overflow: 'hidden',
                        background: '#e5e7eb', border: '1.5px solid #d1d5db',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {hasPlayer && player!.photoMediaId ? (
                          <img src={`/api/media/${player!.photoMediaId}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <span style={{ fontSize: '0.85rem', color: '#9ca3af' }}>?</span>
                        )}
                      </div>
                      <div style={{
                        position: 'absolute', bottom: -2, right: -2,
                        width: 15, height: 15, borderRadius: '50%',
                        background: hasPlayer ? '#00174d' : '#9ca3af',
                        color: '#fff', fontSize: '0.48rem', fontWeight: 900,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: '1.5px solid #fff',
                      }}>{num}</div>
                    </div>

                    {/* Info */}
                    {hasPlayer ? (
                      <div style={{ flex: 1, minWidth: 0, paddingRight: entry?.isCaptain ? 14 : 0 }}>
                        <div style={{
                          fontSize: '0.58rem', color: '#6b7280',
                          lineHeight: 1.1, overflow: 'hidden',
                          textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {player!.firstName}
                        </div>
                        <div style={{
                          fontSize: '0.74rem', fontWeight: 800, color: '#111827',
                          lineHeight: 1.15, textTransform: 'uppercase',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {player!.lastName}
                        </div>
                        <div style={{ fontSize: '0.58rem', color: '#6b7280', lineHeight: 1.4, marginTop: '0.1rem' }}>
                          {(() => {
                            const age = calcAge(player!.birthDate);
                            const parts: string[] = [];
                            if (age !== null) parts.push(`${age} ans`);
                            if (player!.heightCm) parts.push(`${player!.heightCm}cm`);
                            if (player!.weightKg) parts.push(`${player!.weightKg}kg`);
                            return parts.length > 0 ? <div>{parts.join(' · ')}</div> : null;
                          })()}
                          {player!.previousClubName && (
                            <div style={{ color: '#3b82f6', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              ex {player!.previousClubName}{player!.joinedYear ? ` (${player!.joinedYear})` : ''}
                            </div>
                          )}
                          {player!.seasonStats && (() => {
                            const parts = [
                              player!.seasonStats!.starts !== null ? `${player!.seasonStats!.starts} tit.` : null,
                              player!.seasonStats!.tries !== null ? `${player!.seasonStats!.tries} ess.` : null,
                              player!.seasonStats!.minutes !== null ? `${player!.seasonStats!.minutes}'` : null,
                            ].filter(Boolean);
                            return parts.length > 0 ? <div style={{ color: '#16a34a' }}>{parts.join(' · ')}</div> : null;
                          })()}
                          {player!.internationalCaps.length > 0 && (
                            <div style={{ color: '#d97706', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {player!.internationalCaps.map(c => `${c.team} ${c.caps}sel`).join(' · ')}
                            </div>
                          )}
                          {player!.isJiff && (
                            <span style={{
                              display: 'inline-block',
                              background: '#e0f2fe', color: '#0369a1',
                              borderRadius: '2px', padding: '0 3px',
                              fontSize: '0.52rem', fontWeight: 700, lineHeight: '13px',
                            }}>JIFF</span>
                          )}
                        </div>
                        {entry && (
                          <textarea
                            value={playerNotes[entry.playerId] ?? ''}
                            onClick={e => e.stopPropagation()}
                            onMouseEnter={e => e.stopPropagation()}
                            onChange={e => { e.stopPropagation(); setPlayerNotes(prev => ({ ...prev, [entry.playerId]: e.target.value })); }}
                            onBlur={e => void saveNote(entry.playerId, e.target.value)}
                            placeholder="Note..."
                            rows={2}
                            style={{
                              display: 'block',
                              width: '100%',
                              boxSizing: 'border-box',
                              marginTop: '0.25rem',
                              resize: 'none',
                              border: `1px solid ${playerNotes[entry.playerId] ? '#f59e0b' : '#e5e7eb'}`,
                              borderRadius: '3px',
                              padding: '0.15rem 0.3rem',
                              fontSize: '0.6rem',
                              fontFamily: 'inherit',
                              background: playerNotes[entry.playerId] ? '#fffbeb' : '#fafafa',
                              lineHeight: 1.4,
                              outline: 'none',
                              color: '#374151',
                            }}
                          />
                        )}
                      </div>
                    ) : (
                      <div style={{ fontSize: '0.65rem', color: '#9ca3af', paddingTop: '0.5rem' }}>—</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Subs */}
      <div style={{ background: '#1a4a28', padding: '0.5rem', border: '1px solid #2d6035', borderTop: 'none' }}>
        <div style={{ fontSize: '0.7rem', color: '#a8c4e0', marginBottom: '0.35rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Remplaçants
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
          {SUBS.map(num => {
            const player = getPlayer(num);
            return (
              <div
                key={num}
                onClick={() => setPickerSlot(num)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.3rem',
                  background: player ? '#2d6035' : 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  padding: '0.25rem 0.5rem',
                  cursor: 'pointer',
                  minWidth: 36,
                }}
              >
                <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', flexShrink: 0 }}>{num}</span>
                {player && (
                  <>
                    {player.photoMediaId && (
                      <img src={`/api/media/${player.photoMediaId}`} alt="" style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                      <span style={{ color: '#fff', fontSize: '0.7rem', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {player.lastName}
                      </span>
                      <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.62rem', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                        {[
                          calcAge(player.birthDate) !== null ? `${calcAge(player.birthDate)} ans` : null,
                          player.heightCm ? `${player.heightCm}cm` : null,
                          player.weightKg ? `${player.weightKg}kg` : null,
                          player.nationality ?? null,
                        ].filter(Boolean).join(' · ')}
                      </span>
                      {player.previousClubName && (
                        <span style={{ color: '#bfdbfe', fontSize: '0.62rem', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                          ex {player.previousClubName}{player.joinedYear ? ` (${player.joinedYear})` : ''}
                        </span>
                      )}
                      {player.seasonStats && (
                        <span style={{ color: '#bbf7d0', fontSize: '0.62rem', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                          {[
                            player.seasonStats.starts !== null ? `${player.seasonStats.starts} tit.` : null,
                            player.seasonStats.tries !== null ? `${player.seasonStats.tries} ess.` : null,
                            player.seasonStats.minutes !== null ? `${player.seasonStats.minutes}'` : null,
                          ].filter(Boolean).join(' · ')}
                        </span>
                      )}
                      {player.internationalCaps.length > 0 && (
                        <span style={{ color: '#fde68a', fontSize: '0.62rem', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                          {player.internationalCaps.map(c => `${c.team} ${c.caps}sel`).join(' · ')}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      </div>{/* end printRef */}

      {/* Actions */}
      <div style={{
        background: '#fff', border: '1px solid #e5e7eb', borderTop: 'none',
        borderRadius: '0 0 4px 4px', padding: '0.6rem 1rem',
        display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap',
      }}>
        <button
          onClick={() => void save()}
          disabled={saving}
          style={{
            background: saving ? '#93b3cc' : '#1e3a5f',
            color: '#fff', border: 'none', borderRadius: '4px',
            padding: '0.45rem 1.25rem',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: '0.85rem', fontWeight: 600,
          }}
        >
          {saving ? 'Sauvegarde...' : 'Enregistrer'}
        </button>
        {saved && <span style={{ color: '#16a34a', fontSize: '0.82rem' }}>Composition enregistrée</span>}

        {/* Quick actions for assigned players */}
        {entries.filter(e => e.number <= 15).length > 0 && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {entries
              .filter(e => e.number <= 15)
              .sort((a, b) => a.number - b.number)
              .map(e => {
                const p = players.find(pl => pl.id === e.playerId);
                if (!p) return null;
                return (
                  <div
                    key={e.number}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.2rem', fontSize: '0.72rem' }}
                  >
                    <button
                      onClick={() => toggleCaptain(e.number)}
                      title={`Capitaine: ${p.firstName} ${p.lastName}`}
                      style={{
                        background: e.isCaptain ? '#FFD700' : '#f5f5f5',
                        border: '1px solid #ddd', borderRadius: '3px',
                        padding: '0.1rem 0.3rem', cursor: 'pointer',
                        fontSize: '0.7rem', fontWeight: 700,
                        color: e.isCaptain ? '#000' : '#888',
                      }}
                    >
                      {e.number} C
                    </button>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* Player info tooltip on hover */}
      {hoveredInfo && (
        <div style={{
          position: 'fixed',
          left: Math.min(hoveredInfo.rect.right + 12, window.innerWidth - 220),
          top: Math.max(8, Math.min(hoveredInfo.rect.top - 20, window.innerHeight - 320)),
          zIndex: 9999,
          background: '#1e3a5f',
          color: '#fff',
          borderRadius: '10px',
          padding: '0.875rem',
          width: 200,
          boxShadow: '0 6px 24px rgba(0,0,0,0.55)',
          pointerEvents: 'none',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.6rem' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, border: '2px solid rgba(255,255,255,0.4)', background: '#2d547a' }}>
              {hoveredInfo.player.photoMediaId ? (
                <img src={`/api/media/${hoveredInfo.player.photoMediaId}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '1.4rem' }}>?</div>
              )}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.2 }}>
                #{hoveredInfo.num} · {hoveredInfo.player.firstName}
              </div>
              <div style={{ fontWeight: 900, fontSize: '1rem', lineHeight: 1.2, textTransform: 'uppercase' }}>
                {hoveredInfo.player.lastName}
              </div>
              <div style={{ display: 'flex', gap: '0.3rem', marginTop: '0.3rem', flexWrap: 'wrap' }}>
                {hoveredInfo.entry?.isCaptain && (
                  <span style={{ background: '#FFD700', color: '#000', borderRadius: '3px', padding: '0.05rem 0.3rem', fontSize: '0.68rem', fontWeight: 900 }}>Capitaine</span>
                )}
                {hoveredInfo.player.isJiff && (
                  <span style={{ background: '#0369a1', color: '#fff', borderRadius: '3px', padding: '0.05rem 0.3rem', fontSize: '0.68rem', fontWeight: 600 }}>JIFF</span>
                )}
              </div>
            </div>
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.3rem', fontSize: '0.8rem' }}>
            {hoveredInfo.player.position && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'rgba(255,255,255,0.6)' }}>Poste</span>
                <span style={{ fontWeight: 600 }}>{hoveredInfo.player.position}</span>
              </div>
            )}
            {(hoveredInfo.player.heightCm || hoveredInfo.player.weightKg) && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'rgba(255,255,255,0.6)' }}>Gabarit</span>
                <span style={{ fontWeight: 600 }}>
                  {hoveredInfo.player.heightCm ? `${hoveredInfo.player.heightCm} cm` : '—'}
                  {' / '}
                  {hoveredInfo.player.weightKg ? `${hoveredInfo.player.weightKg} kg` : '—'}
                </span>
              </div>
            )}
            {hoveredInfo.player.nationality && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'rgba(255,255,255,0.6)' }}>Nationalité</span>
                <span style={{ fontWeight: 600 }}>{hoveredInfo.player.nationality}</span>
              </div>
            )}
            {calcAge(hoveredInfo.player.birthDate) !== null && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'rgba(255,255,255,0.6)' }}>Âge</span>
                <span style={{ fontWeight: 600 }}>{calcAge(hoveredInfo.player.birthDate)} ans</span>
              </div>
            )}
            {hoveredInfo.player.previousClubName && (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                <span style={{ color: 'rgba(255,255,255,0.6)', flexShrink: 0 }}>Ancien club</span>
                <span style={{ fontWeight: 600, textAlign: 'right' }}>
                  {hoveredInfo.player.previousClubName}
                  {hoveredInfo.player.joinedYear ? ` (${hoveredInfo.player.joinedYear})` : ''}
                </span>
              </div>
            )}
            {hoveredInfo.player.seasonStats && (
              <div style={{ marginTop: '0.3rem', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '0.3rem' }}>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', marginBottom: '0.2rem' }}>Saison en cours</div>
                {[
                  { label: 'Titularisations', val: hoveredInfo.player.seasonStats.starts },
                  { label: 'Essais', val: hoveredInfo.player.seasonStats.tries },
                  { label: 'Minutes', val: hoveredInfo.player.seasonStats.minutes !== null ? `${hoveredInfo.player.seasonStats.minutes}'` : null },
                ].filter(r => r.val !== null).map(r => (
                  <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#bbf7d0' }}>{r.label}</span>
                    <span style={{ fontWeight: 600 }}>{r.val}</span>
                  </div>
                ))}
              </div>
            )}
            {hoveredInfo.player.internationalCaps.length > 0 && (
              <div style={{ marginTop: '0.3rem', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '0.3rem' }}>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', marginBottom: '0.2rem' }}>Sélections</div>
                {hoveredInfo.player.internationalCaps.map(c => (
                  <div key={c.team} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#fde68a', fontWeight: 700 }}>{c.team}</span>
                    <span style={{ fontWeight: 600 }}>{c.caps} sel · {c.points} pts</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Player picker modal */}
      {pickerSlot !== null && (
        <PlayerPicker
          players={players}
          usedPlayerIds={usedPlayerIds}
          currentPlayerId={getEntry(pickerSlot)?.playerId ?? null}
          onSelect={p => assign(pickerSlot, p)}
          onClose={() => setPickerSlot(null)}
          slotNumber={pickerSlot}
        />
      )}

      {/* Right-click context for assigned spots: clear / captain */}
    </div>
  );
}

// ─── Squad column ─────────────────────────────────────────────────────────────

function PlayerRow({ player }: { player: PlayerSummaryDto }): JSX.Element {
  return (
    <div style={{
      display: 'flex', alignItems: 'center',
      padding: '0.5rem 1rem', borderBottom: '1px solid #f0f0f0',
      gap: '0.5rem', fontSize: '0.9rem',
    }}>
      <PlayerAvatar photoMediaId={player.photoMediaId} size={32} />
      <span style={{ flex: '0 0 130px', fontWeight: 600 }}>{player.firstName} {player.lastName}</span>
      <span style={{ flex: '0 0 100px', color: '#555' }}>{player.position ?? '—'}</span>
      <span style={{ flex: '0 0 60px', color: '#888', fontSize: '0.82rem' }}>{player.heightCm ? `${player.heightCm} cm` : '—'}</span>
      <span style={{ flex: '0 0 60px', color: '#888', fontSize: '0.82rem' }}>{player.weightKg ? `${player.weightKg} kg` : '—'}</span>
      {player.isJiff && (
        <span style={{ background: '#e0f2fe', color: '#0369a1', borderRadius: '3px', padding: '0.1rem 0.35rem', fontSize: '0.75rem', fontWeight: 600 }}>JIFF</span>
      )}
    </div>
  );
}

function SquadColumn({ title, players }: { title: string; players: PlayerSummaryDto[] }): JSX.Element {
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, background: '#00174d', color: '#fff', padding: '0.4rem 1rem', margin: 0, borderRadius: '4px 4px 0 0' }}>
        {title} ({players.length})
      </h3>
      {players.length === 0 ? (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderTop: 'none', padding: '1.5rem', textAlign: 'center', color: '#aaa', fontSize: '0.85rem', borderRadius: '0 0 4px 4px' }}>
          Aucun joueur
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 4px 4px', overflow: 'hidden' }}>
          <div style={{ display: 'flex', padding: '0.3rem 1rem', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: '0.75rem', color: '#888', gap: '0.5rem' }}>
            <span style={{ width: 32 }} />
            <span style={{ flex: '0 0 130px' }}>Joueur</span>
            <span style={{ flex: '0 0 100px' }}>Poste</span>
            <span style={{ flex: '0 0 60px' }}>Taille</span>
            <span style={{ flex: '0 0 60px' }}>Poids</span>
          </div>
          {players.map(p => <PlayerRow key={p.id} player={p} />)}
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function MatchPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, logout, accessToken } = useAuth();
  const [match, setMatch] = useState<MatchDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scraping, setScraping] = useState(false);
  const [scrapeJob, setScrapeJob] = useState<ScrapeJobDto | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<'squads' | 'lineup'>('squads');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const homeLineupRef = useRef<HTMLDivElement>(null);
  const awayLineupRef = useRef<HTMLDivElement>(null);
  const [exportingPdf, setExportingPdf] = useState(false);

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const loadMatch = useCallback(async () => {
    if (!id) return null;
    try {
      const data = await apiGet<MatchDetailDto>(`/matches/${id}`);
      setMatch(data);
      if (data.lastScrapeJob) setScrapeJob(data.lastScrapeJob);
      return data.lastScrapeJob ?? null;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
      return null;
    }
  }, [id]);

  const pollJob = useCallback((jobId: string) => {
    stopPolling();
    pollRef.current = setInterval(() => {
      void (async () => {
        try {
          const job = await apiGet<ScrapeJobDto>(`/scrape-jobs/${jobId}`);
          setScrapeJob(job);
          if (job.status === 'Succeeded' || job.status === 'Failed' || job.status === 'Partial') {
            stopPolling();
            setScraping(false);
            await loadMatch();
          }
        } catch { stopPolling(); setScraping(false); }
      })();
    }, 2000);
  }, [stopPolling, loadMatch]);

  useEffect(() => { return () => stopPolling(); }, [stopPolling]);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      const job = await loadMatch();
      setLoading(false);
      if (job && (job.status === 'Queued' || job.status === 'Running')) {
        setScraping(true);
        pollJob(job.id);
      }
    })();
  }, [loadMatch, pollJob]);

  const handleScrape = useCallback(async () => {
    if (!id || scraping) return;
    try {
      setScraping(true);
      setScrapeJob(null);
      const job = await apiPost<ScrapeJobDto>(`/matches/${id}/scrape`);
      setScrapeJob(job);
      pollJob(job.id);
    } catch (err) {
      setScraping(false);
      setError(err instanceof Error ? err.message : 'Erreur lors du scrape');
    }
  }, [id, scraping, pollJob]);

  const handleRefreshResults = useCallback(async () => {
    if (!match || refreshing) return;
    try {
      setRefreshing(true);
      await apiPost(`/competitions/${match.competitionId}/calendar/refresh`);
      await loadMatch();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la mise à jour');
    } finally {
      setRefreshing(false);
    }
  }, [match, refreshing, loadMatch]);

  const exportPdf = useCallback(async () => {
    if (!homeLineupRef.current || !awayLineupRef.current || !match) return;
    setExportingPdf(true);
    try {
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const margin = 3;
      const availW = pageW - margin * 2;
      for (const [i, el] of ([[0, homeLineupRef.current], [1, awayLineupRef.current]] as [number, HTMLDivElement][])) {
        if (i > 0) pdf.addPage();
        const canvas = await html2canvas(el, { useCORS: true, scale: 2, backgroundColor: '#f9fafb' });
        const ratio = availW / canvas.width;
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', margin, margin, availW, canvas.height * ratio);
      }
      pdf.save(`${match.homeClubName} - ${match.awayClubName}.pdf`);
    } finally {
      setExportingPdf(false);
    }
  }, [match]);

  const progressPct = scrapeJob?.progressTotal
    ? Math.round((scrapeJob.progressDone / scrapeJob.progressTotal) * 100)
    : null;

  const hasPlayers = match && (match.homePlayers.length > 0 || match.awayPlayers.length > 0);

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      <nav style={{ background: '#1e3a5f', color: '#fff', padding: '0 1.75rem', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
          <button onClick={() => navigate('/')} style={{ background: 'transparent', border: 'none', color: '#a8c4e0', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500, padding: 0 }}>
            ← Calendrier
          </button>
          <span style={{ color: '#2d5a8e', fontSize: '1rem' }}>|</span>
          <img src={cantalLogo} alt="Pierroinfo" style={{ height: '38px', width: 'auto' }} />
          <span style={{ fontWeight: 700, fontSize: '1.1rem', letterSpacing: '0.02em' }}>Pierroinfo</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', fontSize: '0.875rem' }}>
          <span style={{ color: '#a8c4e0', fontWeight: 500 }}>{user?.displayName}</span>
          {user?.role === 'Admin' && (
            <button onClick={() => navigate('/admin')} style={{ background: 'transparent', border: '1px solid #7c3aed', color: '#c4b5fd', padding: '0.3rem 0.75rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 }}>
              Admin
            </button>
          )}
          <button onClick={() => void logout()} style={{ background: 'transparent', border: '1px solid #a8c4e0', color: '#a8c4e0', padding: '0.3rem 0.75rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 }}>
            Déconnexion
          </button>
        </div>
      </nav>

      <main style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem 1rem' }}>
        {loading && <div style={{ textAlign: 'center', color: '#888', padding: '3rem' }}>Chargement...</div>}
        {error && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '0.75rem 1rem', borderRadius: '6px', marginBottom: '1rem' }}>{error}</div>}

        {!loading && match && (
          <>
            {/* Match header */}
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem', textAlign: 'center' }}>
              {match.round && (
                <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{match.round}</div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2rem' }}>
                <span style={{ fontSize: '1.3rem', fontWeight: 700, flex: 1, textAlign: 'right' }}>{match.homeClubName}</span>
                <div style={{ minWidth: '120px', textAlign: 'center' }}>
                  {match.status === 'Finished' && match.homeScore !== null && match.awayScore !== null ? (
                    <span style={{ fontSize: '1.8rem', fontWeight: 900, color: '#1e3a5f' }}>{match.homeScore} — {match.awayScore}</span>
                  ) : (
                    <span style={{ fontSize: '0.9rem', color: '#666' }}>{match.kickoffUtc ? formatKickoff(match.kickoffUtc) : 'à venir'}</span>
                  )}
                </div>
                <span style={{ fontSize: '1.3rem', fontWeight: 700, flex: 1, textAlign: 'left' }}>{match.awayClubName}</span>
              </div>
              {match.kickoffUtc && match.status !== 'Finished' && (
                <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>{formatKickoff(match.kickoffUtc)}</div>
              )}
              {accessToken && (
                <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => void handleRefreshResults()}
                    disabled={refreshing || scraping}
                    style={{ background: refreshing ? '#93b3cc' : '#16a34a', color: '#fff', border: 'none', padding: '0.5rem 1.25rem', borderRadius: '4px', cursor: (refreshing || scraping) ? 'not-allowed' : 'pointer', fontSize: '0.9rem' }}
                  >
                    {refreshing ? 'Mise à jour...' : 'Màj résultat'}
                  </button>
                  <button
                    onClick={() => void handleScrape()}
                    disabled={scraping || refreshing}
                    style={{ background: scraping ? '#93b3cc' : '#1e3a5f', color: '#fff', border: 'none', padding: '0.5rem 1.25rem', borderRadius: '4px', cursor: (scraping || refreshing) ? 'not-allowed' : 'pointer', fontSize: '0.9rem' }}
                  >
                    {scraping ? 'Scrape en cours...' : 'Scraper les données'}
                  </button>
                </div>
              )}
            </div>

            {/* Scrape progress */}
            {scrapeJob && (
              <div style={{ marginBottom: '1.5rem', background: '#fff', borderRadius: '6px', border: '1px solid #e5e7eb', padding: '0.75rem 1rem' }}>
                <div style={{ fontSize: '0.85rem', color: '#555', marginBottom: '0.4rem' }}>
                  {scrapeJob.status === 'Queued' && 'En attente...'}
                  {scrapeJob.status === 'Running' && `Scrape en cours — ${scrapeJob.progressDone}/${scrapeJob.progressTotal ?? '?'} joueurs`}
                  {scrapeJob.status === 'Succeeded' && 'Données scraped avec succès.'}
                  {scrapeJob.status === 'Partial' && `Terminé avec des erreurs : ${scrapeJob.errors ?? ''}`}
                  {scrapeJob.status === 'Failed' && `Echec : ${scrapeJob.errors ?? ''}`}
                </div>
                {progressPct !== null && (
                  <div style={{ background: '#e5e7eb', borderRadius: '3px', height: '8px', overflow: 'hidden' }}>
                    <div style={{ background: scrapeJob.status === 'Failed' ? '#ef4444' : '#1e3a5f', width: `${progressPct}%`, height: '100%', transition: 'width 0.3s' }} />
                  </div>
                )}
              </div>
            )}

            {/* Tabs */}
            {hasPlayers && accessToken && (
              <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                {(['squads', 'lineup'] as const).map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    style={{
                      padding: '0.45rem 1.25rem', border: 'none', borderRadius: '4px', cursor: 'pointer',
                      fontSize: '0.88rem', fontWeight: activeTab === tab ? 700 : 400,
                      background: activeTab === tab ? '#00174d' : '#e5e7eb',
                      color: activeTab === tab ? '#fff' : '#555',
                    }}
                  >
                    {tab === 'squads' ? 'Effectifs' : 'Composition'}
                  </button>
                ))}
                {activeTab === 'lineup' && (
                  <button
                    onClick={() => void exportPdf()}
                    disabled={exportingPdf}
                    style={{
                      marginLeft: 'auto',
                      padding: '0.45rem 1.25rem', border: 'none', borderRadius: '4px',
                      cursor: exportingPdf ? 'not-allowed' : 'pointer',
                      fontSize: '0.88rem', fontWeight: 600,
                      background: exportingPdf ? '#93b3cc' : '#7c3aed',
                      color: '#fff',
                    }}
                  >
                    {exportingPdf ? 'Export...' : 'Exporter PDF'}
                  </button>
                )}
              </div>
            )}

            {/* Squads tab */}
            {(!hasPlayers || activeTab === 'squads') && (
              <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                <SquadColumn title={match.homeClubName} players={match.homePlayers} />
                <SquadColumn title={match.awayClubName} players={match.awayPlayers} />
              </div>
            )}

            {/* Lineup tab — composition view */}
            {hasPlayers && activeTab === 'lineup' && accessToken && (
              <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <PitchLineupEditor matchId={match.id} side="Home" clubName={match.homeClubName} players={match.homePlayers} printRef={homeLineupRef} />
                <PitchLineupEditor matchId={match.id} side="Away" clubName={match.awayClubName} players={match.awayPlayers} printRef={awayLineupRef} />
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
